import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Percent, Clock, TrendingDown } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";
import { useLanguage } from "@/hooks/useLanguage";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import { formatPriceWithCurrency } from "@/utils/currency";
import { ProductRating } from "@/components/ProductRating";

interface Deal {
  id: string;
  product_id: string;
  discount_percentage: number;
  end_date: string;
  badge_text: string;
  featured: boolean;
  product: {
    id: string;
    title: string;
    description: string;
    price_cents: number;
    currency: string;
    images: string[];
    average_rating?: number;
    review_count?: number;
  };
}

export default function Deals() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterFeatured, setFilterFeatured] = useState(false);
  const navigate = useNavigate();
  const { displayCurrency, convertPrice } = useCurrency();
  const { t } = useLanguage();

  useEffect(() => {
    fetchDeals();
  }, [filterFeatured]);

  const fetchDeals = async () => {
    setLoading(true);
    let query = supabase
      .from("deals")
      .select(`
        *,
        product:products(*)
      `)
      .eq("active", true)
      .lte("start_date", new Date().toISOString())
      .gte("end_date", new Date().toISOString())
      .order("discount_percentage", { ascending: false });

    if (filterFeatured) {
      query = query.eq("featured", true);
    }

    const { data, error } = await query;

    if (!error && data) {
      // Fetch ratings for each product in deals
      const dealsWithRatings = await Promise.all(
        (data as any[]).map(async (deal: any) => {
          try {
            const [ratingResult, countResult] = await Promise.all([
              supabase.rpc('get_product_average_rating', { product_uuid: deal.product.id }),
              supabase.rpc('get_product_review_count', { product_uuid: deal.product.id })
            ]);
            
            return {
              ...deal,
              product: {
                ...deal.product,
                average_rating: ratingResult.data,
                review_count: countResult.data
              }
            };
          } catch (error) {
            console.error('Error fetching ratings for product:', deal.product.id, error);
            return {
              ...deal,
              product: {
                ...deal.product,
                average_rating: 0,
                review_count: 0
              }
            };
          }
        })
      );
      
      setDeals(dealsWithRatings);
    }
    setLoading(false);
  };

  const calculateDiscountedPrice = (originalPrice: number, discount: number) => {
    return originalPrice * (1 - discount / 100);
  };

  const getTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}${t('deals.days_left')} ${hours}${t('deals.hours_left')}`;
    if (hours > 0) return `${hours}${t('deals.hours_left')}`;
    return t('deals.expires_soon');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 container py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white rounded-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <TrendingDown className="h-12 w-12" />
            <div>
              <h1 className="text-4xl font-bold mb-2">{t('deals.title')}</h1>
              <p className="text-lg text-white/90">
                {t('deals.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Button
            variant={filterFeatured ? "default" : "outline"}
            onClick={() => setFilterFeatured(!filterFeatured)}
            className="gap-2"
          >
            <Percent className="h-4 w-4" />
            {filterFeatured ? t('deals.all_deals') : t('deals.featured_deals')}
          </Button>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-card rounded-lg h-96 animate-pulse" />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">{t('deals.no_deals')}</h3>
            <p className="text-muted-foreground">
              {t('deals.no_deals_message')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {deals.map((deal) => {
              return <DealCard key={deal.id} deal={deal} navigate={navigate} convertPrice={convertPrice} displayCurrency={displayCurrency} calculateDiscountedPrice={calculateDiscountedPrice} getTimeRemaining={getTimeRemaining} t={t} />;
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

interface DealCardProps {
  deal: Deal;
  navigate: any;
  convertPrice: (amount: number, fromCurrency: string, toCurrency: string) => number;
  displayCurrency: string;
  calculateDiscountedPrice: (originalPrice: number, discount: number) => number;
  getTimeRemaining: (endDate: string) => string;
  t: (key: string) => string;
}

function DealCard({ deal, navigate, convertPrice, displayCurrency, calculateDiscountedPrice, getTimeRemaining, t }: DealCardProps) {
  const { title: translatedTitle } = useProductTranslation(deal.product.id, deal.product.title, deal.product.description || '');
  
  const originalPrice = deal.product.price_cents;
  const discountedPriceInCents = Math.round(calculateDiscountedPrice(originalPrice, deal.discount_percentage));
  
  return (
    <div
      className="bg-card rounded-lg overflow-hidden border hover:shadow-lg transition-shadow cursor-pointer group"
      onClick={() => navigate(`/product/${deal.product.id}`)}
    >
      {/* Image */}
      <div className="relative aspect-square bg-muted">
        <img
          src={deal.product.images?.[0] || "/placeholder.svg"}
          alt={translatedTitle}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
        
        {/* Discount Badge */}
        <div className="absolute top-2 left-2">
          <Badge className="bg-destructive text-destructive-foreground text-lg font-bold px-3 py-1">
            -{deal.discount_percentage}%
          </Badge>
        </div>
        
        {/* Limited Time Badge */}
        <div className="absolute top-2 right-2">
          <Badge variant="secondary" className="bg-white/90 text-foreground">
            {deal.badge_text}
          </Badge>
        </div>

        {/* Featured Badge */}
        {deal.featured && (
          <div className="absolute bottom-2 left-2">
            <Badge className="bg-primary text-primary-foreground">
              {t('deals.featured')}
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {translatedTitle}
        </h3>
        
        <ProductRating 
          rating={deal.product.average_rating || 0} 
          reviewCount={deal.product.review_count || 0}
          size="sm"
        />
        
        {/* Prices */}
        <div className="mb-3 mt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-primary">
              {formatPriceWithCurrency(discountedPriceInCents, deal.product.currency, displayCurrency, convertPrice)}
            </span>
            <span className="text-sm text-muted-foreground line-through">
              {formatPriceWithCurrency(originalPrice, deal.product.currency, displayCurrency, convertPrice)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('deals.lowest_price')} {formatPriceWithCurrency(originalPrice, deal.product.currency, displayCurrency, convertPrice)}
          </p>
        </div>

        {/* Time Remaining */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>{getTimeRemaining(deal.end_date)}</span>
        </div>
      </div>
    </div>
  );
}
